import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

export interface Destination {
  id: string;
  name: string;
  description: string;
  image: string;
  sort_order: number;
}

interface DestinationRow extends Destination {
  name_vi: string | null;
  description_vi: string | null;
}

export function useDestinations() {
  const [rows, setRows] = useState<DestinationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchDestinations() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
          .from('vietnamtravel_destinations')
          .select('id, name, name_vi, description, description_vi, image, sort_order')
          .order('sort_order', { ascending: true });

        if (err) throw err;
        if (!cancelled) setRows((data ?? []) as DestinationRow[]);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load destinations');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDestinations();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const destinations: Destination[] = rows.map((r) => ({
    id: r.id,
    name: loc(language, r.name, r.name_vi),
    description: loc(language, r.description ?? '', r.description_vi),
    image: r.image ?? '',
    sort_order: r.sort_order ?? 0,
  }));

  return { destinations, loading, error, refresh };
}